import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {P24RegistrationData} from "../models/p24-registration-data.model";
import {VetClinic} from "../models/vet-clinic.model";

/**
 * Serwis warstwy danych obsługujący proces rejestracji klinik jako
 * zweryfikowanych sprzedawców (Merchants) w systemie Przelewy24.
 * Zarządza formularzami rejestracyjnymi, wysyłką danych do operatora
 * oraz sprawdzaniem statusu weryfikacji.
 */
@Injectable({
  providedIn: 'root'
})
export class P24RegistrationDataService{

  constructor(
    private apiService: ApiService,
    public commonUtils : CommonUtils,
  ) {
  }

  /**
   * Pobiera z bazy danych aktualnie wypełniony formularz rejestracyjny P24
   * dla wskazanego klienta.
   *
   * @param vclientUUID Identyfikator klienta.
   * @param p24RegistrationDataUUID Unikalny identyfikator konkretnego formularza rejestracyjnego.
   * @returns Observable emitujący zinstancjonowany obiekt z danymi rejestrowymi.
   */
  getp24RegistrationData(vclientUUID: string, p24RegistrationDataUUID: string): Observable<P24RegistrationData> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/p24-reg/${vclientUUID}/${p24RegistrationDataUUID}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<P24RegistrationData>(new P24RegistrationData(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje wprowadzony przez klinikę formularz z danymi rozliczeniowymi i rejestrowymi.
   * Krok przygotowawczy przed właściwym wysłaniem wniosku do operatora.
   *
   * @param billingData Obiekt zawierający dane firmy, reprezentantów i rachunki bankowe.
   * @param vclinicUUID Identyfikator docelowej kliniki weterynaryjnej.
   * @returns Observable emitujący zaktualizowany obiekt formularza rejestracyjnego.
   */
  addp24RegistrationData(billingData: P24RegistrationData, vclinicUUID: string): Observable<P24RegistrationData> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/p24-reg/${vclinicUUID}`, billingData).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<P24RegistrationData>(new P24RegistrationData(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje właściwy proces przekazania (Push) danych do zewnętrznego API Przelewy24.
   * Powoduje to utworzenie wniosku weryfikacyjnego po stronie operatora płatności.
   *
   * @param vclientUUID Identyfikator klienta.
   * @param p24RegistrationDataUUID Identyfikator uzupełnionego formularza.
   * @returns Observable emitujący zaktualizowany obiekt formularza.
   */
  registerMerchant(vclientUUID: string, p24RegistrationDataUUID: string): Observable<P24RegistrationData> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/p24-reg-send/${vclientUUID}/${p24RegistrationDataUUID}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<P24RegistrationData>(new P24RegistrationData(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Weryfikuje aktualny status rozpatrywania wniosku
   * bezpośrednio u operatora Przelewy24.
   *
   * @param vclientUUID Identyfikator klienta.
   * @param p24RegistrationDataUUID Identyfikator wniosku.
   * @returns Observable emitujący obiekt Kliniki.
   */
  verifyMerchantStatus(vclientUUID: string, p24RegistrationDataUUID: string): Observable<VetClinic> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/p24-reg-check/${vclientUUID}/${p24RegistrationDataUUID}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

}
